import { ChangeDetectionStrategy, Component, ElementRef, computed, effect, inject, input, output, signal, DOCUMENT } from '@angular/core';
import moment, { Moment } from 'moment';
import { SharedModule } from '@shared/shared.module';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { HdContentLanguageService } from '../../bodygraph/hd-content-language.service';

/**
 * Geboortedatum-invoer: een getypt `dd-mm-jjjj`-veld (primair — de meeste mensen
 * vullen manueel in) met een kalender-icoon IN het veld (zoals het klokje van de
 * tijd-invoer). Het icoon opent de `e-date-picker` als zwevende helper (overlay,
 * vergroot het formulier niet). Emit een geldige `Moment` of `undefined`.
 */
@Component({
  selector: 'hd-birth-date-input',
  standalone: true,
  imports: [SharedModule, TranslocoModule],
  templateUrl: './hd-birth-date-input.component.html',
  styleUrl: './hd-birth-date-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdBirthDateInputComponent {
  /** Optionele begindatum (edit-modus); de parent kent deze al, dus geen emit bij init. */
  readonly initialDate = input<Moment | undefined>(undefined);
  readonly valueChange = output<Moment | undefined>();

  protected readonly text = signal('');
  protected readonly showCalendar = signal(false);
  /** Waarde voor de kalender-helper (de huidige geldige datum, of leeg). */
  protected readonly calendarValue = signal<Moment | undefined>(undefined);

  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly documentRef = inject(DOCUMENT);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly translatedText = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      datePlaceholder: translate('humanDesign.chart.createDialog.datePlaceholder'),
      dateAria: translate('humanDesign.chart.createDialog.dateAria'),
      openCalendar: translate('humanDesign.chart.createDialog.openCalendar'),
      openCalendarTooltip: translate('humanDesign.chart.createDialog.openCalendarTooltip'),
    };
  });

  protected readonly calendarLocale = computed(() => this.contentLang.effectiveLang());

  constructor() {
    // Klik buiten de zwevende kalender sluit hem — net als de native tijd-picker.
    effect(onCleanup => {
      if (!this.showCalendar()) {
        return;
      }
      const onPointerDown = (event: Event): void => {
        const target = event.target as Node | null;
        if (target && this.hostEl.contains(target)) {
          return;
        }
        this.showCalendar.set(false);
      };
      this.documentRef.addEventListener('pointerdown', onPointerDown, true);
      onCleanup(() => this.documentRef.removeEventListener('pointerdown', onPointerDown, true));
    });
  }

  ngOnInit(): void {
    const initial = this.initialDate();
    if (initial?.isValid()) {
      this.text.set(initial.format('DD-MM-YYYY'));
      this.calendarValue.set(initial);
    }
  }

  /** Ruwe invoer → enkel cijfers, auto opgemaakt als dd-mm-jjjj met streepjes. */
  protected onInput(raw: string): void {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    let formatted = digits;
    if (digits.length > 4) {
      formatted = `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
    } else if (digits.length > 2) {
      formatted = `${digits.slice(0, 2)}-${digits.slice(2)}`;
    }
    this.text.set(formatted);
    this.emitFromText();
  }

  protected toggleCalendar(): void {
    this.showCalendar.update(open => !open);
  }

  /** Kalender-helper koos een datum → tekstveld invullen + emit + helper sluiten. */
  protected onCalendarChange(value: Moment | undefined): void {
    if (value && moment.isMoment(value)) {
      this.text.set(value.format('DD-MM-YYYY'));
      this.calendarValue.set(value);
      this.valueChange.emit(value);
    }
    this.showCalendar.set(false);
  }

  private emitFromText(): void {
    const parsed = this.parse(this.text());
    this.calendarValue.set(parsed);
    this.valueChange.emit(parsed);
  }

  /** Strikte dd-MM-yyyy parse; onvolledige of ongeldige invoer → undefined. */
  parse(text: string): Moment | undefined {
    if (text.length < 10) {
      return undefined;
    }
    const parsed = moment(text, 'DD-MM-YYYY', true);
    return parsed.isValid() ? parsed : undefined;
  }
}
